import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { cn } from "@/lib/utils";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, Cpu, Waypoints, Lightbulb } from 'lucide-react'; // Updated icons

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 }, // Reduced y for subtlety
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: "easeOut" } // Slightly faster
  }
};

const staggerChildren = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 } // Slightly faster stagger
  }
};

// Removed pulseAnimation for minimality, can be re-added if desired for specific elements

export default function SegmentOverview({ viewport, isRTL }) {
  const { t } = useTranslation();
  // Removed isMobile and isTablet from viewport destructuring as they are not used directly in this simplified version
  // const { isMobile = false, isTablet = false } = viewport || { isMobile: false, isTablet: false };

  const segmentsData = [
    {
      key: 'iot',
      icon: <Lightbulb size={28} className="text-primary" />, // Lucide icon
      color: 'hsl(var(--primary))',
    },
    {
      key: 'edge',
      icon: <Cpu size={28} className="text-green-500" />, // Lucide icon, example different color
      color: 'hsl(var(--green-500, 145 58% 45%))',
    },
    {
      key: 'fleet',
      icon: <Waypoints size={28} className="text-sky-500" />, // Lucide icon, example different color
      color: 'hsl(var(--sky-500, 200 89% 60%))',
    }
  ];

  // Merging translations directly into segmentsData for cleaner mapping
  const segments = segmentsData.map(segment => ({
    ...segment,
    title: t(`homepage.segments.${segment.key}.title`),
    description: t(`homepage.segments.${segment.key}.description`),
    bullets: [
      t(`homepage.segments.${segment.key}.bullet1`),
      t(`homepage.segments.${segment.key}.bullet2`),
      t(`homepage.segments.${segment.key}.bullet3`),
      t(`homepage.segments.${segment.key}.bullet4`),
    ]
  })); 

  return (
    <section
      id="segments"
      className="relative py-20 md:py-28 lg:py-32 bg-background/95" // Simplified background
    >
      {/* Optional: Subtle background pattern if desired, otherwise remove for pure minimality */}
      {/* <div className="absolute inset-0 bg-grid-pattern-dots opacity-5 pointer-events-none" /> */}

      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={staggerChildren}
          className="text-center mb-12 md:mb-20 max-w-2xl mx-auto"
        >
          <motion.div variants={fadeInUp}>
            <Badge
              variant="outline"
              className="mb-3 px-3 py-1 text-xs font-medium rounded-full border-primary/30 bg-primary/10 text-primary"
            >
              {t('homepage.segments.badge', 'Core Capabilities')}
            </Badge>
          </motion.div>

          <motion.div variants={fadeInUp}>
            <h2 className="text-3xl md:text-4xl font-bold mb-4 tracking-tight text-foreground">
              {t('homepage.segments.title', 'Unlock New Efficiencies')}
            </h2>
          </motion.div>

          <motion.div variants={fadeInUp}>
            <p className="text-md md:text-lg text-muted-foreground max-w-xl mx-auto">
              {t('homepage.segments.subtitle', 'Discover how our platform modules can be tailored to address your specific industry challenges and drive growth.')}
            </p>
          </motion.div>
        </motion.div>

        {/* Grid of Segment Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {segments.map((segment, index) => (
            <motion.div
              key={segment.key} // Using unique key from data
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: isRTL ? 0.1 : 0.2 }} // Adjust amount based on RTL for better trigger
              variants={fadeInUp}
              // custom={index} // Not strictly needed with whileInView and fadeInUp on each item
              className="flex" // Ensures consistent height if cards have varying content
            >
              <Card className={cn(
                "flex flex-col w-full group", // Ensure full width, flex column for content structure
                "border-border/20 bg-background/80 backdrop-blur-sm", // Minimal card appearance
                "hover:border-primary/40 hover:shadow-xl hover:shadow-primary/10", // Subtle hover effects
                "transition-all duration-300 ease-in-out",
                "rounded-xl overflow-hidden" // Consistent rounding
              )}>
                <CardHeader className="pt-6 pb-4 px-6">
                  <div className="flex items-center gap-4 mb-3">
                    <div
                      className={cn(
                        "flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center",
                        "bg-opacity-10 group-hover:bg-opacity-20 transition-colors duration-300"
                      )}
                      style={{ backgroundColor: `color-mix(in srgb, ${segment.color} 15%, transparent)` }}
                    >
                      {segment.icon} 
                    </div>
                    <CardTitle className="text-lg font-semibold text-foreground group-hover:text-primary transition-colors duration-300">
                      {segment.title}
                    </CardTitle>
                  </div>
                  <CardDescription className="text-sm text-muted-foreground line-clamp-3">
                    {segment.description}
                  </CardDescription>
                </CardHeader>

                <CardContent className="pt-2 pb-6 px-6 flex-grow">
                  <ul className="space-y-2.5 mt-2 text-sm">
                    {segment.bullets.map((bullet, i) => (
                      <li key={i} className="flex items-start">
                        <CheckCircle className="h-4 w-4 mr-2.5 mt-0.5 flex-shrink-0" style={{ color: segment.color }} />
                        <span className="text-muted-foreground group-hover:text-foreground transition-colors duration-200">
                          {bullet}
                        </span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
